/**
 * @module sagas/Privacy.js
 */
import getIn from 'lodash/get';
import { all, select, take } from 'redux-saga/effects';

import { AudioPlayerActions } from '../constants/AudioPlayer';
import { getInDomain } from '../domains/externalUtils';
import { getCurrentListenerIdFromState } from '../lib/ListenerUtils';
import Logger from '../tools/Logger';

import { taskManagerSpawnFactory } from './Helpers/SpawnedTaskManager';

const log = Logger.child({ component: 'sagas/Privacy' });
const spawn = taskManagerSpawnFactory();
const LOOP = true;

function ttmSourceChangedAction(action) {
  return (
    action.type === getInDomain('stats.constants.StatsActions.TTM_START') &&
    action.data?.get('ttmAction') === getInDomain('stats.constants.TTMActions.SOURCE_CHANGED')
  );
}

function audioPlayTrackOnDemandAction(action) {
  return action.type === AudioPlayerActions.AUDIO_PLAY_TRACK && action.payload.playlistId;
}

function setPrivacyWindowData(privacyData) {
  Object.entries(privacyData).forEach(([_key, value], index) => {
    const keyName = `_pxParam${index + 1}`;

    if (value) {
      log.info(`${keyName}: ${value} added to window`);
      window[keyName] = value;
    } else if (window[keyName]) {
      log.info(`${keyName}: removed from window`);
      delete window[keyName];
    }
  });
}

function getPrivacyData(action, annotations = null) {
  const listenerId = getIn(action, 'listenerId', null);
  if (!listenerId) return null;

  const track = action.payload?.track;
  const pandoraId = track?.get('pandoraId', null);

  return {
    param1: listenerId,
    param2: pandoraId,
    param3: track?.get('sourceId', null),
    param4: track?.get('artistName', null),
    param5: track?.get('albumName', null),
    param6: track?.get('songName', null),
    param7: track?.get('duration', null),
    param8: annotations?.get('artistId', null),
    param9: annotations?.get('albumId', null),
    param10: annotations?.get('shareableUrlPath', null),
  };
}

export function* initPrivacy() {
  let listenerId = null;
  let payload = null;

  while (LOOP) {
    if (!listenerId) {
      listenerId = yield select(getCurrentListenerIdFromState);
      if (listenerId) {
        const annotations = null;
        const privacyData = getPrivacyData({ listenerId }, annotations);
        setPrivacyWindowData(privacyData);
      }
    }

    const action = yield take(audioPlayTrackOnDemandAction);
    if (action.type === AudioPlayerActions.AUDIO_PLAY_TRACK) {
      payload = action.payload || action.data;
    }

    if (payload) {
      const pandoraId = payload?.track?.get('pandoraId', null);

      const annotations = pandoraId
        ? yield select(getInDomain('catalog.utils.getAnnotations'), pandoraId)
        : null;

      const privacyData = getPrivacyData({ listenerId, payload }, annotations);
      setPrivacyWindowData(privacyData);
      payload = null;
    }

    yield take(ttmSourceChangedAction);
  }
}

export default function* privacySaga() {
  yield all([spawn(initPrivacy)]);
}
